import * as React from 'react';
import { useCallback, useRef, useState } from 'react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../Command';
import { CirclePlus } from 'lucide-react';
import Button from '../../components/Button';
import ButtonAdornment from '../../components/ButtonAdornment';
import { DataTableFilterOption } from './types';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { v4 as uuidv4 } from 'uuid';

/**
 * What the picker offers. `id` is OPTIONAL here — and a catalogue is better off without one:
 * the id that matters is the uuid minted per pick below, which is what lets two clauses on
 * the same field be edited and removed independently.
 */
export type AddFilterOption = Omit<DataTableFilterOption, 'id'> & { id?: string };

interface DataTableFilterComboboxProps {
  options: AddFilterOption[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<DataTableFilterOption[]>>;
  onSelect: () => void;
  disabled?: boolean;
}

export function AddFilterDropdown({ options, setSelectedOptions, onSelect, disabled }: DataTableFilterComboboxProps) {
  const [open, setOpen] = useState(false);
  // Whether THIS close is the tail of a pick. Radix fires `onCloseAutoFocus` on every close,
  // and only the pick path has somewhere better to put focus — see the handler below.
  const closingAfterPick = useRef(false);

  const handleSelect = useCallback(
    (option: AddFilterOption) => {
      // A catalogue entry can be offered but not pickable — e.g. an operator set that
      // would match nothing or everything for the surface it is offered on.
      if (option.disabled) return;

      closingAfterPick.current = true;
      setOpen(false);
      // `id` LAST: the minted uuid must win even when the catalogue entry carries one of
      // its own, otherwise two clauses on the same field share an id and FilterItem —
      // which edits and removes by id — silently hits both.
      setSelectedOptions((prev) => [
        ...prev,
        {
          ...option,
          id: uuidv4(),
        },
      ]);
      if (onSelect) {
        onSelect();
      }
    },
    [onSelect, setSelectedOptions],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger disabled={disabled} asChild>
        <Button
          variant="contained"
          size="small"
          color="secondary"
          disabled={disabled}
          className="flex justify-between gap-2 !py-1.5"
          startAdornment={
            <ButtonAdornment>
              <CirclePlus className="h-4 w-4" />
            </ButtonAdornment>
          }
        >
          Add filter
        </Button>
      </PopoverTrigger>
      {/* A PICK does not pull focus back to the trigger: it exists to open that clause's
          editor, and that editor focuses itself as it mounts; Radix's default focus restore
          lands a beat later, outside the new editor, and Radix reads its own focus move as
          an outside interaction and dismisses the editor again.

          Every OTHER close — Escape, a click outside — leaves focus nowhere, so suppressing
          the restore there dropped it to <body>: a keyboard user who tabbed to "Add filter",
          opened it and changed their mind had to tab from the top of the panel again. */}
      <PopoverContent
        // BOUNDED, because one disabled row's explanation must not decide how
        // wide a list of field names is. The reason is rendered inline (a
        // disabled cmdk row is `pointer-events: none`, so a tooltip on it can
        // never open), and unbounded a single sentence — "Contents do not carry a
        // measurement date you can filter on yet" — stretched a six-item menu to
        // 612px. Figma 12463:39490 draws it at ~245.
        className="w-full max-w-[280px] p-0"
        align="start"
        onCloseAutoFocus={(event) => {
          if (!closingAfterPick.current) return;
          closingAfterPick.current = false;
          event.preventDefault();
        }}
      >
        <Command>
          <CommandInput placeholder="Filter by..." />
          <CommandList className="max-h-full">
            <CommandEmpty>No item found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={String(option.value)}
                  className="flex gap-2 capitalize"
                  // cmdk keys its registry — and its notion of which row is highlighted — by
                  // `value`, so this has to be the entry's unique identity, not the words it
                  // reads: two rows that happen to READ the same collapse into one cmdk item
                  // and highlight together, with no React warning to say so.
                  value={String(option.value)}
                  // …and `keywords` is what makes the row searchable by what it READS, so
                  // typing "Channels" finds "Channels & Accounts" WITHOUT giving up the wire
                  // name, which is what the saved rule and the GraphQL args call it.
                  keywords={[option.label]}
                  disabled={option.disabled}
                  title={option.disabled ? option.disabledReason : undefined}
                  onSelect={() => handleSelect(option)}
                >
                  {option.icon}
                  {/* Rendered inline rather than as a tooltip: the row is not hoverable
                      (`pointer-events: none` while disabled), so a tooltip would never open.

                      STACKED under the label, not beside it. Beside it the reason took
                      half a 280px panel and "Last Measurement Date" wrapped to three
                      lines while the sentence wrapped to four alongside — two ragged
                      columns where there should be a field name. Under it, the name reads
                      as a name and the explanation as an explanation. The `title` above
                      still carries the whole sentence for a pointer. */}
                  {option.disabled && option.disabledReason ? (
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span>{option.label}</span>
                      <span className="text-xs normal-case leading-snug text-muted-foreground">
                        {option.disabledReason}
                      </span>
                    </span>
                  ) : (
                    option.label
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
